import { createSystem, defaultConfig } from '@chakra-ui/react';

import { BREAKPOINT_VALUES } from '../constants';

export const ROCKET_THEME = createSystem(defaultConfig, {
  globalCss: {
    // Preserve semantic text styling
    'em, i': {
      fontStyle: 'italic',
    },
    'strong, b': {
      fontWeight: 'bold',
    },
  },
  theme: {
    breakpoints: BREAKPOINT_VALUES,
  },
});

const SHARED_GLOBAL_CSS = {
  'em, i': {
    fontStyle: 'italic',
  },
  'strong, b': {
    fontWeight: 'bold',
  },
};

/**
 * Chakra's rem-based tokens assume a 16px root font-size. bbcedit sets
 * `html { font-size: 62.5% }` (10px root, MUI-era convention), which would
 * render every rocket component at 62.5% of its intended size. Scale all
 * rem token values by 16/10 so they resolve to the intended pixel sizes.
 */
const LEGACY_ROOT_SCALE = 1.6;

function scaleRemTokens(tokens: unknown): unknown {
  if (typeof tokens === 'string') {
    return tokens.replace(
      /(-?\d*\.?\d+)rem/g,
      (_, n: string) => `${Math.round(Number(n) * LEGACY_ROOT_SCALE * 10000) / 10000}rem`
    );
  }
  if (Array.isArray(tokens)) {
    return tokens.map(scaleRemTokens);
  }
  if (tokens && typeof tokens === 'object') {
    return Object.fromEntries(
      Object.entries(tokens as Record<string, unknown>).map(([k, v]) => [k, scaleRemTokens(v)])
    );
  }
  return tokens;
}

const scaledTokenGroups = Object.fromEntries(
  (['fontSizes', 'spacing', 'sizes', 'radii'] as const).map((group) => [
    group,
    scaleRemTokens(defaultConfig.theme?.tokens?.[group] ?? {}),
  ])
);

/**
 * Theme for hosts that still render legacy UI under a 62.5% root font-size
 * (bbcedit). Differences from ROCKET_THEME:
 * - preflight off: Chakra's global reset would restyle the existing MUI pages
 * - rem tokens scaled x1.6 to compensate for the 10px root
 * Retire this (switch the host to ROCKET_THEME) when the host drops the
 * 62.5% root, i.e. once MUI is gone.
 */
/**
 * The config behind ROCKET_THEME_LEGACY_ROOT, exported separately so hosts can
 * compose extra config into it. `createSystem` is variadic and merges, so a host
 * that wants to add brand recipes does:
 *
 *   createSystem(defaultConfig, ROCKET_THEME_LEGACY_ROOT_CONFIG, { theme: { recipes } })
 *
 * without having to duplicate the preflight/rem-scaling handling above.
 */
export const ROCKET_THEME_LEGACY_ROOT_CONFIG = {
  preflight: false,
  globalCss: SHARED_GLOBAL_CSS,
  theme: {
    breakpoints: BREAKPOINT_VALUES,
    tokens: scaledTokenGroups,
  },
};

export const ROCKET_THEME_LEGACY_ROOT = createSystem(
  defaultConfig,
  ROCKET_THEME_LEGACY_ROOT_CONFIG
);

/** @deprecated superseded by ROCKET_THEME_LEGACY_ROOT (adds legacy root-font-size token scaling). */
export const ROCKET_THEME_NO_PREFLIGHT = ROCKET_THEME_LEGACY_ROOT;

export default ROCKET_THEME;
